import {
    IconStyle,
    PaintStyle,
    PaletteColorType,
    PREDEFINED_PALETTES,
    ShapeColorStyle,
    ShapeType,
    ThemeStyleWeight
} from "common/constants";
import { builtInFonts } from "common/fontConstants";
import { THEME_DEFAULTS, themes } from "common/themes";
import { getStaticUrl } from "js/config";
import { Palette } from "js/core/models/palette";
import StorageModel from "js/core/storage/storageModel";
import { StyleSheet } from "js/core/styleSheet";
import { formatter } from "../../../common/formatter";
import { _, Backbone } from "js/vendor";
import { getDefaultSlides } from "../../../common/themes/defaultDefaultSlides";
import { AiImagePresetStyles } from "../../../common/aiConstants";
import { fontManager } from "../services/fonts";

export function getThemeUrl(path, style, version) {
    const url = path ? `/themes/v${version}/${path}/${style}.scss` : `/themes/v${version}/${style}.scss`;
    return getStaticUrl(url);
}

const Theme = StorageModel.extend({

    root: "user_themes",

    defaults: THEME_DEFAULTS,

    hasMigrationChanges: false,

    getFontIds: function() {
        return Object.entries(this.attributes)
            .filter(([key, value]) => key.endsWith("FontId"))
            .map(([key, value]) => value);
    },

    getColors: function() {
        let themeColors = this.get("colors");
        if (!themeColors) {
            let palette = this.getPalette(this.get("palette_name"));
            themeColors = _.clone(palette.colors);
        }
        return themeColors;
    },

    getAccentColorsCount() {
        return Object.keys(this.palette.getAccentColors()).length;
    },

    getPalette: function(paletteId) {
        let palette = _.find(PREDEFINED_PALETTES, { id: paletteId });
        if (!palette) {
            // fallback for old theme support
            palette = _.find(PREDEFINED_PALETTES, { old_id: paletteId });
        }
        if (!palette) {
            palette = _.find(PREDEFINED_PALETTES, { id: "colorful" });
        }
        return _.clone(palette);
    },

    isBespoke: function() {
        return this.has("styleSheet");
    },

    getAiImageStyle: function() {
        const imageStyle = this.get("imageStyle");
        if (!imageStyle) {
            return AiImagePresetStyles[0].style;
        }
        if (imageStyle == "custom") {
            return this.get("imageStylePrompt");
        } else {
            return imageStyle;
        }
    },

    getStyles(version, reloadCache = false) {
        if (!this.getStylesPromises) {
            this.getStylesPromises = {};
        }

        if (reloadCache) {
            delete this.getStylesPromises[version];
        }

        if (!this.getStylesPromises[version]) {
            const getVersionedThemeUrl = (path, style) => getThemeUrl(path, style, version);

            this.getStylesPromises[version] = (async () => {
                const decorationStyles = {
                    outlined: await this.loadStyleSheet(getVersionedThemeUrl("elements", "outlined")),
                    muted: await this.loadStyleSheet(getVersionedThemeUrl("elements", "muted")),
                    filled: await this.loadStyleSheet(getVersionedThemeUrl("elements", "filled")),
                    fillAndStroke: await this.loadStyleSheet(getVersionedThemeUrl("elements", "fillAndStroke")),
                };

                ////// Load frankenstein stylesheet from theme properties //////
                const styleSheet = await this.loadStyleSheet(getVersionedThemeUrl(null, "default"));

                this.loadStyleSheet(getVersionedThemeUrl("backgrounds", this.get("styleBackground")), styleSheet);

                const decoration = this.get("styleDecoration");
                await this.loadStyleSheet(getVersionedThemeUrl("decorations", decoration), styleSheet);

                await this.loadStyleSheet(getVersionedThemeUrl("elements", this.get("styleElementStyle")), styleSheet);

                if (this.get("styleShape") == ShapeType.NONE) {
                    await this.loadStyleSheet(getVersionedThemeUrl("shapes", "shape-none"), styleSheet);
                } else {
                    await this.loadStyleSheet(getVersionedThemeUrl("shapes", "shape"), styleSheet);
                }

                await this.loadStyleSheet(getVersionedThemeUrl("weights", this.get("styleWeight")), styleSheet);

                await this.loadFonts(styleSheet);

                await this.loadStyleSheet(getVersionedThemeUrl("effects", this.get("styleEffect")), styleSheet);

                if (this.has("customStyles")) {
                    await this.mergeCssIntoStylesheet(styleSheet, this.get("customStyles"));
                }

                styleSheet.applyStyleSheetVariables(styleSheet.variables);
                styleSheet.processFillAndStroke();
                //////

                // Apply any variables to the values in the decorationStyles
                for (const style of Object.keys(decorationStyles)) {
                    const decorationStyle = decorationStyles[style];
                    for (const property of Object.keys(decorationStyle)) {
                        const value = decorationStyle[property];
                        if (value && typeof (value) == "string" && value.startsWith("$")) {
                            const varName = value.split(" ")[0];
                            decorationStyle[property] = decorationStyle[property].replace(varName, styleSheet.variables[varName.substr(1)]);
                        }
                    }
                }

                return { styleSheet, decorationStyles };
            })();
        }

        return this.getStylesPromises[version];
    },

    async loadTheme() {
        if (!this.has("colors")) {
            let predefinedPalette = _.find(PREDEFINED_PALETTES, { id: this.get("palette_name") || "colorful" });
            if (predefinedPalette) {
                this.update({ colors: predefinedPalette.colors }, { replaceKeys: true });
            }
        }

        this.palette = new Palette(this);

        // Ensure collection colors are valid (some collection color records can be corrupted because of a bug)
        if (this.has("collectionColors") && Array.isArray(this.get("collectionColors"))) {
            const validCollectionColors = _.cloneDeep(this.get("collectionColors"))
                .filter(collectionColor => {
                    if (!collectionColor) {
                        return false;
                    }
                    if (!collectionColor.id) {
                        return false;
                    }
                    if (collectionColor.type === "sequence") {
                        return Array.isArray(collectionColor.colors) && collectionColor.colors.length > 0;
                    }
                    if (collectionColor.type === "gradient") {
                        return !!collectionColor.startColor && !!collectionColor.endColor;
                    }
                    return true;
                });

            this.update({ collectionColors: validCollectionColors }, { replaceKeys: true });
        }

        // Create collectionColors if it doesn't exist, if it's false it means there are no collection colors defined (which is valid)
        // this should only happen for legacy themes created before collection colors feature was added
        if (!this.has("collectionColors")) {
            this.update({
                collectionColors: [{
                    id: "colorful",
                    type: "sequence",
                    colors: Object.keys(this.palette.getSlideColors())
                }]
            }, { replaceKeys: true });
        }

        // create default default slides
        if (!this.has("defaultSlides")) {
            this.update({ defaultSlides: getDefaultSlides() }, { replaceKeys: true });
        }

        await this.migrate();
    },

    async migrate() {
        //// February 2018 migration to split styleBackground options into defaultBackgroundColor ////
        const styleBackground = this.get("styleBackground");
        const defaultBackgroundColor = this.get("defaultBackgroundColor");
        const removedStyleBackgrounds = {
            "light": PaletteColorType.BACKGROUND_LIGHT,
            "dark": PaletteColorType.BACKGROUND_DARK,
            "color": PaletteColorType.THEME
        };

        if (["gradient", "reverseGradient"].includes(styleBackground) && !defaultBackgroundColor) {
            this.update({ defaultBackgroundColor: "theme" });
            this.hasMigrationChanges = true;
        } else if (removedStyleBackgrounds[styleBackground]) {
            let defaultBackground = removedStyleBackgrounds[styleBackground];
            this.update({
                "defaultBackgroundColor": defaultBackground,
                "styleBackground": "none"
            });
            this.hasMigrationChanges = true;
        }

        //// FONTS MIGRATION OLDEST -> PREVIOUS ////
        if (!this.get("fontHeaderFontId") && (!this.get("fontHeader") || !this.get("fontHeaderRegular"))) {
            const headerFontPath = `${this.get("styleTitleFont")}-header-${this.get("styleFontWeight")}`;
            const styleSheet = await this.loadStyleSheet(getThemeUrl("fonts", headerFontPath, 9));
            this.setObsoleteFontVariables("header", headerFontPath, styleSheet.variables);
        }
        if (!this.get("fontTitleFontId") && (!this.get("fontTitle") || !this.get("fontTitleRegular"))) {
            let titleFont = this.get("styleTitleFont");
            // special case for existing montserrat themes which actually use playfair as title font
            if (titleFont === "playfair") {
                titleFont = "raleway";
            }
            const titleFontPath = `${titleFont}-title-${this.get("styleFontWeight")}`;
            const styleSheet = await this.loadStyleSheet(getThemeUrl("fonts", titleFontPath, 9));
            this.setObsoleteFontVariables("title", titleFontPath, styleSheet.variables);
        }
        if (!this.get("fontBodyFontId") && (!this.get("fontBody") || !this.get("fontBodyRegular"))) {
            const bodyFontPath = `${this.get("styleBodyFont")}-body`;
            const styleSheet = await this.loadStyleSheet(getThemeUrl("fonts", bodyFontPath, 9));
            this.setObsoleteFontVariables("body", bodyFontPath, styleSheet.variables);
        }

        //// FONTS MIGRATION PREVIOUS -> CURRENT ////
        for (const style of ["Header", "Title", "Body"]) {
            if (!this.get(`font${style}FontId`)) {
                // Marking so we have changes first
                this.hasMigrationChanges = true;

                const regularFontFaceName = this.get(`font${style}Regular`);
                const emphasizedFontFaceName = this.get(`font${style}Emphasized`);

                // Looking up built in font
                for (const [fontId, fontDefinition] of Object.entries(builtInFonts)) {
                    for (const fontStyle of fontDefinition.styles) {
                        if (fontStyle.fontFaceName === regularFontFaceName) {
                            this.update({
                                [`font${style}FontId`]: fontId,
                                [`font${style}Weight`]: fontStyle.weight
                            });
                        }

                        if (fontStyle.fontFaceName === emphasizedFontFaceName) {
                            this.update({
                                [`font${style}BoldWeight`]: fontStyle.weight
                            });
                        }
                    }
                }

                // Custom font
                if (!this.get(`font${style}FontId`)) {
                    try {
                        const [fontId, fontStyle] = regularFontFaceName.split("/");
                        const emphasizedFontStyle = emphasizedFontFaceName.split("/")[1];
                        this.update({
                            [`font${style}FontId`]: fontId,
                            [`font${style}Weight`]: fontStyle === "bold" ? 700 : 400,
                            [`font${style}BoldWeight`]: emphasizedFontStyle === "bold" ? 700 : 400
                        });
                    } catch (err) {
                        // Handle silently
                    }
                }

                // Cleaning up obsolete fields
                this.update({
                    [`font${style}`]: null,
                    [`font${style}Regular`]: null,
                    [`font${style}Italic`]: null,
                    [`font${style}Emphasized`]: null,
                    [`font${style}EmphasizedItalic`]: null
                });
            }
        }
    },

    async loadFonts(styleSheet) {
        // Composing style sheets
        styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("header", "Header"));
        styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("display", "Header"));
        styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("title", "Title"));
        styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("body", "Body"));
        if (this.has("fontLabelFontId")) {
            styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("label", "Label"));
        }

        // optimistically preload the fonts instead of waiting for BaseElement (plus the emphasized header font is outside styles and wouldn't be loaded)
        const headerFont = await fontManager.loadFont(this.get("fontHeaderFontId"));
        await Promise.all(headerFont.styles.map(style => style.loadCssFont()));
        if (this.has("fontHeaderEmphasizedFontId")) {
            const headerEmphasizedFont = await fontManager.loadFont(this.get("fontHeaderEmphasizedFontId"));
            await Promise.all(headerEmphasizedFont.styles.map(style => style.loadCssFont()));
        }
        const titleFont = await fontManager.loadFont(this.get("fontTitleFontId"));
        await Promise.all(titleFont.styles.map(style => style.loadCssFont()));
        const bodyFont = await fontManager.loadFont(this.get("fontBodyFontId"));
        await Promise.all(bodyFont.styles.map(style => style.loadCssFont()));

        if (this.has("fontLabelFontId")) {
            const labelFont = await fontManager.loadFont(this.get("fontLabelFontId"));
            await Promise.all(labelFont.styles.map(style => style.loadCssFont()));
        }

        return styleSheet;
    },

    /**
     * Sets obsolete font variables for migration
     */
    setObsoleteFontVariables(fontType, fontFamily, variables) {
        const capitalizedFontType = formatter.capitalizeFirstLetter(fontType);
        const fontUpdates = {
            [`font${capitalizedFontType}`]: fontFamily,
            [`font${capitalizedFontType}Regular`]: variables[`${fontType}FontRegular`],
            [`font${capitalizedFontType}Italic`]: variables[`${fontType}FontItalic`],
            [`font${capitalizedFontType}Emphasized`]: variables[`${fontType}FontEmphasized`],
            [`font${capitalizedFontType}EmphasizedItalic`]: variables[`${fontType}FontEmphasizedItalic`],
            [`font${capitalizedFontType}LineHeight`]: variables[`${fontType}FontLineHeight`],
            [`font${capitalizedFontType}LetterSpacing`]: variables[`${fontType}FontLetterSpacing`],
            [`font${capitalizedFontType}TextTransform`]: variables[`${fontType}FontTextTransform`] || "auto",
            [`font${capitalizedFontType}Scaling`]: variables[`${fontType}FontScaling`] || 100
        };

        if (variables[`${fontType}FontStrokeThickness`]) {
            fontUpdates[`font${capitalizedFontType}StrokeThickness`] = variables[`${fontType}FontStrokeThickness`];
        }
        this.update(fontUpdates);
    },

    async buildCustomFontCss(fontKey, textStyle) {
        return `
        $${fontKey}FontId: ${this.get(`font${textStyle}FontId`)};
        $${fontKey}FontItalic: ${this.get(`font${textStyle}FontItalic`) ?? false};
        $${fontKey}FontWeight: ${this.get(`font${textStyle}Weight`)};
        ${this.get(`font${textStyle}BoldWeight`) ? `$${fontKey}FontBoldWeight: ${this.get(`font${textStyle}BoldWeight`)};` : ""}
        $${fontKey}FontLineHeight: ${this.get(`font${textStyle}LineHeight`)};
        $${fontKey}FontLetterSpacing: ${this.get(`font${textStyle}LetterSpacing`)};
        $${fontKey}FontTextTransform: ${this.get(`font${textStyle}TextTransform`)};
        $${fontKey}FontScaling: ${this.get(`font${textStyle}Scaling`)};
        $${fontKey}FontStrokeThickness: ${this.get(`font${textStyle}StrokeThickness`)};
        `;
    },

    async loadStyleSheet(cssFilePath, baseStyleSheet = null) {
        try {
            const cssText = await fetch(cssFilePath).then(response => response.text());
            return this.mergeCssIntoStylesheet(baseStyleSheet, cssText);
        } catch (err) {
            // Fallback to default
            return baseStyleSheet;
        }
    },

    mergeCssIntoStylesheet(styleSheet = null, css) {
        if (_.isEmpty(css)) {
            return styleSheet ?? new StyleSheet(this, "");
        }

        if (styleSheet) {
            _.merge(styleSheet, new StyleSheet(this, css));
        } else {
            styleSheet = new StyleSheet(this, css);
        }
        return styleSheet;
    },

    sync: function() {
        //void
    }
});

const BuiltInThemes = Backbone.Collection.extend({
    model: Theme,

    initialize: function() {
        Object.values(themes).forEach(theme => {
            this.add(new Theme(theme, { disconnected: true, autoLoad: false }));
        });
    }
});

export { BuiltInThemes, IconStyle, PaintStyle, PREDEFINED_PALETTES, ShapeColorStyle, ShapeType, Theme, ThemeStyleWeight };

